// Centralized JSON-LD entity graph for buddhaka.com.
//
// One source of truth for the three canonical nodes:
//   Person       — Buddha KA          #buddha-ka
//   Organization — KAfrequency        #kafrequency
//   WebSite      — buddhaka.com       #website
//
// Every route that emits structured data must REFERENCE these @ids
// (via personRef() / orgRef() / websiteRef()) rather than redefining the
// nodes, so no page ever carries conflicting duplicate entities.
//
// Facts here are repository-sourced only (src/lib/content.ts, /about, /press).

import buddhaKaPortrait from "@/assets/buddha-ka-official.png.asset.json";
import { STREAMING_LINKS } from "@/lib/content";
import { SITE_URL, SITE_NAME, absoluteUrl } from "@/lib/site";
import { CANONICAL_SHORT_DESCRIPTION, entityLastModified, factValue } from "@/lib/entity";

/* ------------------------------------------------------------------ ids */

export const PERSON_ID = `${SITE_URL}/#buddha-ka`;
export const ORG_ID = `${SITE_URL}/#kafrequency`;
export const WEBSITE_ID = `${SITE_URL}/#website`;
export const LOGO_ID = `${SITE_URL}/#logo`;

/** Stable @ids for the hub CollectionPage nodes referenced across the graph. */
export const MUSIC_CATALOG_ID = `${SITE_URL}/music#catalog`;
export const NEWSROOM_ID = `${SITE_URL}/news#newsroom`;
export const BOOKS_LIBRARY_ID = `${SITE_URL}/books#library`;
export const ACTIVATIONS_ID = `${SITE_URL}/activations#activations`;
export const ACADEMY_ID = `${SITE_URL}/academy#academy`;
export const TIMELINE_ID = `${SITE_URL}/timeline#timeline`;

/** KAfrequency as the imprint / record label behind every release. */
export const IMPRINT_ID = `${SITE_URL}/#imprint`;
export const imprintRef = (): Ref => ({ "@id": IMPRINT_ID });

/** Stable @ids for music entities, so every page references the same node. */
export const releaseId = (slug: string) => `${SITE_URL}/music/${slug}#release`;
export const songId = (releaseSlug: string, songSlug: string) =>
  `${SITE_URL}/music/${releaseSlug}/song/${songSlug}#song`;



export const PORTRAIT_URL = absoluteUrl(buddhaKaPortrait.url);
export const PORTRAIT_ALT = "Buddha KA — official portrait";

export type Ref = { "@id": string };
export type Node = Record<string, unknown>;

export const personRef = (): Ref => ({ "@id": PERSON_ID });
export const orgRef = (): Ref => ({ "@id": ORG_ID });
export const websiteRef = (): Ref => ({ "@id": WEBSITE_ID });

/* ------------------------------------------------------- canonical nodes */

const SAME_AS = Object.values(STREAMING_LINKS).filter(Boolean) as string[];

/** Canonical short description — governed by the central entity registry. */
export const PERSON_DESCRIPTION = CANONICAL_SHORT_DESCRIPTION;

/** Genuine last-changed date for the identity facts. Never build time. */
export const ENTITY_DATE_MODIFIED = entityLastModified();

export const ORG_DESCRIPTION =
  "KAfrequency — an artist-owned creative ecosystem uniting music, books, daily activations, visual worlds, and consciousness systems.";

function personNode(): Node {
  return {
    // Multi-typed: one identity node that can legally carry MusicGroup
    // properties (album / track / recordLabel) emitted by the catalog pages.
    "@type": ["Person", "MusicGroup"],
    "@id": PERSON_ID,
    name: "Buddha KA",

    alternateName: ["Curtis Shores"],
    givenName: "Curtis",
    familyName: "Shores",
    url: SITE_URL,
    mainEntityOfPage: { "@id": `${SITE_URL}/about#profilepage` },
    image: { "@id": LOGO_ID },
    description: PERSON_DESCRIPTION,
    birthPlace: { "@type": "Place", name: "Chicago, Illinois, USA" },
    nationality: "American",
    jobTitle: factValue("person.roles").jobTitle,
    knowsAbout: [
      "KAfrequency",
      "KA Flow",
      "Hip-hop",
      "Spiritual science",
      "Kemetic wisdom",
      "Consciousness studies",
      "Daily activations",
    ],
    founder: orgRef(),
    affiliation: orgRef(),
    memberOf: orgRef(),
    recordLabel: imprintRef(),
    subjectOf: [
      { "@id": MUSIC_CATALOG_ID },
      { "@id": NEWSROOM_ID },
      { "@id": BOOKS_LIBRARY_ID },
      { "@id": ACTIVATIONS_ID },
      { "@id": ACADEMY_ID },
      { "@id": `${SITE_URL}/about#profilepage` },
      { "@id": `${SITE_URL}/press#presskit` },
      { "@id": TIMELINE_ID },
    ],
    sameAs: SAME_AS,
  };

}

function organizationNode(): Node {
  return {
    "@type": "Organization",
    "@id": ORG_ID,
    name: "KAfrequency",
    alternateName: "Buddha KA · KAfrequency",
    url: SITE_URL,
    logo: {
      "@type": "ImageObject",
      "@id": LOGO_ID,
      url: PORTRAIT_URL,
      caption: PORTRAIT_ALT,
    },
    image: { "@id": LOGO_ID },
    founder: personRef(),
    description: ORG_DESCRIPTION,
    sameAs: SAME_AS,
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "Media and business inquiries",
      url: `${SITE_URL}/contact`,
      availableLanguage: "English",
    },
  };
}

/**
 * The imprint that publishes the catalog. Modelled as a MusicLabel child of
 * KAfrequency so `recordLabel` references resolve inside the same graph.
 */
function imprintNode(): Node {
  return {
    "@type": ["Organization", "MusicLabel"],
    "@id": IMPRINT_ID,
    name: "KAfrequency",
    url: SITE_URL,
    parentOrganization: orgRef(),
    founder: personRef(),
    description:
      "KAfrequency — the artist-owned imprint that writes, records, and releases every Buddha KA project.",
  };
}

function webSiteNode(): Node {
  return {
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    name: SITE_NAME,
    url: SITE_URL,
    publisher: orgRef(),
    creator: personRef(),
    about: personRef(),
    inLanguage: "en",
    hasPart: [
      { "@id": MUSIC_CATALOG_ID },
      { "@id": NEWSROOM_ID },
      { "@id": BOOKS_LIBRARY_ID },
      { "@id": ACTIVATIONS_ID },
      { "@id": ACADEMY_ID },
      { "@id": TIMELINE_ID },
    ],
  };
}

/** The sitewide @graph. Emitted exactly once, from `__root.tsx`. */
export function siteGraph(): Node {
  return {
    "@context": "https://schema.org",
    "@graph": [personNode(), organizationNode(), imprintNode(), webSiteNode()],

  };
}

/* ----------------------------------------------------------- page helpers */

/** Wrap a JSON-LD object into a TanStack `head().scripts` entry. */
export function ldScript(node: Node) {
  return { type: "application/ld+json", children: JSON.stringify(node) } as const;
}

/**
 * A page node (WebPage / ProfilePage / CollectionPage / ContactPage …)
 * already wired to the WebSite and, optionally, its main entity.
 */
export function pageNode(opts: {
  type: "WebPage" | "ProfilePage" | "CollectionPage" | "ContactPage" | "AboutPage" | "Blog";
  id: string;
  url: string;
  name: string;
  description?: string;
  image?: string;
  imageCaption?: string;
  mainEntity?: Node | Ref;
  about?: Array<Node | Ref>;
  extra?: Node;
}): Node {
  const { type, id, url, name, description, image, imageCaption, mainEntity, about, extra } = opts;
  return {
    "@context": "https://schema.org",
    "@type": type,
    "@id": id,
    url,
    name,
    ...(description ? { description } : {}),
    inLanguage: "en",
    isPartOf: websiteRef(),
    ...(image
      ? {
          primaryImageOfPage: {
            "@type": "ImageObject",
            url: image,
            ...(imageCaption ? { caption: imageCaption } : {}),
          },
        }
      : {}),
    ...(mainEntity ? { mainEntity } : {}),
    ...(about ? { about } : {}),
    ...(extra ?? {}),
  };
}

/** BreadcrumbList from `[label, path]` pairs. Home is prepended automatically. */
export function breadcrumbs(trail: Array<[string, string]>): Node {
  const items: Array<[string, string]> = [["Home", "/"], ...trail];
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map(([name, path], i) => ({
      "@type": "ListItem",
      position: i + 1,
      name,
      item: absoluteUrl(path),
    })),
  };
}

/** ItemList of real, existing URLs — used by collection/hub pages. */
export function itemList(items: Array<{ path: string; name: string }>): Node {
  return {
    "@type": "ItemList",
    numberOfItems: items.length,
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: absoluteUrl(it.path),
      name: it.name,
    })),
  };
}

/* ------------------------------------------------------- academy entities */

/**
 * A `Course` node for an Academy domain or learning path. Always taught by
 * Buddha KA and provided by KAfrequency, so the graph stays one entity deep.
 */
export function courseNode(opts: {
  id: string;
  url: string;
  name: string;
  description?: string;
  /** Path-level Courses declare the domain Course they belong to. */
  partOfCourseId?: string;
  /** e.g. "beginner" | "intermediate" | "advanced" | "master" */
  educationalLevel?: string;
  /** Lesson URLs + titles, emitted as `hasPart`. */
  lessons?: Array<{ path: string; name: string }>;
  extra?: Node;
}): Node {
  const { id, url, name, description, partOfCourseId, educationalLevel, lessons, extra } = opts;
  return {
    "@context": "https://schema.org",
    "@type": "Course",
    "@id": id,
    url,
    name,
    ...(description ? { description } : {}),
    inLanguage: "en",
    isPartOf: partOfCourseId ? { "@id": partOfCourseId } : websiteRef(),
    provider: orgRef(),
    publisher: orgRef(),
    author: personRef(),
    creator: personRef(),
    about: personRef(),
    ...(educationalLevel ? { educationalLevel } : {}),
    ...(lessons?.length
      ? {
          hasPart: lessons.map((l) => ({
            "@type": "LearningResource",
            url: absoluteUrl(l.path),
            name: l.name,
          })),
          numberOfItems: lessons.length,
        }
      : {}),
    ...(extra ?? {}),
  };
}

/** A `LearningResource` node for a single Academy lesson. */
export function learningResourceNode(opts: {
  id: string;
  url: string;
  name: string;
  description?: string;
  /** The domain (or path) Course this lesson belongs to. */
  partOfCourseId?: string;
  educationalLevel?: string;
  /** ISO 8601 duration, e.g. "PT25M". */
  timeRequired?: string;
  keywords?: string[];
  learningObjectives?: string[];
  wordCount?: number;
  datePublished?: string;
  dateModified?: string;
  image?: string;
  extra?: Node;
}): Node {
  const {
    id, url, name, description, partOfCourseId, educationalLevel,
    timeRequired, keywords, learningObjectives, wordCount,
    datePublished, dateModified, image, extra,
  } = opts;
  return {
    "@context": "https://schema.org",
    "@type": "LearningResource",
    "@id": id,
    url,
    name,
    headline: name,
    ...(description ? { description } : {}),
    inLanguage: "en",
    learningResourceType: "Lesson",
    isPartOf: partOfCourseId ? { "@id": partOfCourseId } : websiteRef(),
    author: personRef(),
    creator: personRef(),
    publisher: orgRef(),
    provider: orgRef(),
    about: personRef(),
    ...(educationalLevel ? { educationalLevel } : {}),
    ...(timeRequired ? { timeRequired } : {}),
    ...(keywords?.length ? { keywords } : {}),
    ...(learningObjectives?.length ? { teaches: learningObjectives } : {}),
    ...(typeof wordCount === "number" && wordCount > 0 ? { wordCount } : {}),
    ...(datePublished ? { datePublished } : {}),
    ...(dateModified ? { dateModified } : {}),
    ...(image ? { image: absoluteUrl(image) } : {}),
    ...(extra ?? {}),
  };
}

/** Minutes → ISO 8601 duration, or undefined when there is no real value. */
export function isoMinutes(min?: number | null): string | undefined {
  return typeof min === "number" && min > 0 ? `PT${Math.round(min)}M` : undefined;
}

/** `robots: noindex, follow` meta entry for drafts and unresolved routes. */
export const NOINDEX_META = { name: "robots", content: "noindex, follow" } as const;

/* --------------------------------------------------------- music entities */

/** Shape the music schema helpers need — a structural subset of `Release`. */
export type SchemaRelease = {
  slug: string;
  title: string;
  type: string;
  year?: number;
  releaseDate?: string;
  statement?: string;
  coverUrl?: string;
  links?: Partial<Record<string, string>>;
  tracks?: Array<{ title: string; slug?: string; duration?: string }>;
};

/** "3:42" → "PT3M42S". Returns undefined when the value is not a duration. */
export function isoTrackDuration(raw?: string | null): string | undefined {
  if (!raw) return undefined;
  const m = /^(\d+):([0-5]\d)$/.exec(raw.trim());
  return m ? `PT${Number(m[1])}M${Number(m[2])}S` : undefined;
}

/** Verified streaming/profile URLs for one release — never fabricated. */
function releaseSameAs(r: SchemaRelease): string[] {
  return Object.values(r.links ?? {}).filter(
    (v): v is string => typeof v === "string" && /^https?:\/\//i.test(v),
  );
}

const isAlbumLike = (type: string) => type !== "Single";

/**
 * A `MusicAlbum` (albums / EPs / mixtapes) or `MusicRecording` (singles) node
 * for one release, wired to the artist, the imprint, and the music catalog.
 */
export function musicReleaseNode(r: SchemaRelease, opts?: { withTracks?: boolean }): Node {
  const url = absoluteUrl(`/music/${r.slug}`);
  const image = r.coverUrl ? absoluteUrl(r.coverUrl) : undefined;
  const sameAs = releaseSameAs(r);
  const published = r.releaseDate ?? (r.year ? String(r.year) : undefined);
  return {
    "@type": isAlbumLike(r.type) ? "MusicAlbum" : "MusicRecording",
    "@id": releaseId(r.slug),
    name: r.title,
    url,
    mainEntityOfPage: url,
    inLanguage: "en",
    byArtist: personRef(),
    creator: personRef(),
    publisher: imprintRef(),
    recordLabel: imprintRef(),
    isPartOf: { "@id": MUSIC_CATALOG_ID },
    ...(r.statement ? { description: r.statement } : {}),
    ...(isAlbumLike(r.type)
      ? { albumProductionType: "https://schema.org/StudioAlbum", albumReleaseType: albumReleaseType(r.type) }
      : {}),
    ...(image ? { image } : {}),
    ...(published ? { datePublished: published } : {}),
    ...(sameAs.length ? { sameAs } : {}),
    ...(sameAs.length
      ? {
          potentialAction: sameAs.map((target) => ({
            "@type": "ListenAction",
            target: { "@type": "EntryPoint", urlTemplate: target, actionPlatform: target },
          })),
        }
      : {}),
    ...(opts?.withTracks && r.tracks?.length
      ? {
          numTracks: r.tracks.length,
          track: r.tracks.map((t, i) => ({
            "@type": "MusicRecording",
            ...(t.slug ? { "@id": songId(r.slug, t.slug), url: absoluteUrl(`/music/${r.slug}/song/${t.slug}`) } : {}),
            position: i + 1,
            name: t.title,
            ...(isoTrackDuration(t.duration) ? { duration: isoTrackDuration(t.duration) } : {}),
            byArtist: personRef(),
            inAlbum: { "@id": releaseId(r.slug) },
          })),
        }
      : {}),
  };
}

function albumReleaseType(type: string): string {
  if (type === "EP") return "https://schema.org/EPRelease";
  if (type === "Single") return "https://schema.org/SingleRelease";
  return "https://schema.org/AlbumRelease";
}

/** A single track page node, wired to its album and the artist. */
export function musicRecordingNode(opts: {
  releaseSlug: string;
  releaseTitle: string;
  songSlug: string;
  name: string;
  position?: number;
  description?: string;
  duration?: string;
  audioUrl?: string;
  hasLyrics?: boolean;
  datePublished?: string;
  image?: string;
}): Node {
  const url = absoluteUrl(`/music/${opts.releaseSlug}/song/${opts.songSlug}`);
  const duration = isoTrackDuration(opts.duration);
  return {
    "@type": "MusicRecording",
    "@id": songId(opts.releaseSlug, opts.songSlug),
    name: opts.name,
    url,
    mainEntityOfPage: url,
    inLanguage: "en",
    byArtist: personRef(),
    creator: personRef(),
    publisher: imprintRef(),
    recordLabel: imprintRef(),
    inAlbum: { "@id": releaseId(opts.releaseSlug), name: opts.releaseTitle },
    isPartOf: { "@id": MUSIC_CATALOG_ID },
    ...(typeof opts.position === "number" ? { position: opts.position } : {}),
    ...(opts.description ? { description: opts.description } : {}),
    ...(duration ? { duration } : {}),
    ...(opts.audioUrl ? { audio: { "@type": "AudioObject", contentUrl: absoluteUrl(opts.audioUrl) } } : {}),
    ...(opts.hasLyrics
      ? { recordingOf: { "@type": "MusicComposition", name: opts.name, lyricist: personRef(), composer: personRef() } }
      : {}),
    ...(opts.datePublished ? { datePublished: opts.datePublished } : {}),
    ...(opts.image ? { image: absoluteUrl(opts.image) } : {}),
  };
}

/**
 * Graph patch that hangs the full discography off the existing artist node.
 * Same `@id` as the sitewide Person, so crawlers merge instead of duplicating.
 */
export function discographyPatch(releases: SchemaRelease[]): Node {
  const albums = releases.filter((r) => isAlbumLike(r.type));
  const singles = releases.filter((r) => !isAlbumLike(r.type));
  return {
    "@type": ["Person", "MusicGroup"],
    "@id": PERSON_ID,
    name: "Buddha KA",
    url: SITE_URL,
    recordLabel: imprintRef(),
    ...(albums.length ? { album: albums.map((r) => ({ "@id": releaseId(r.slug) })) } : {}),
    ...(singles.length ? { track: singles.map((r) => ({ "@id": releaseId(r.slug) })) } : {}),
  };
}

/** The whole catalog as one graph — reused by /music and the JSON-LD feed. */
export function discographyGraph(releases: SchemaRelease[]): Node {
  return {
    "@context": "https://schema.org",
    "@graph": [
      discographyPatch(releases),
      ...releases.map((r) => musicReleaseNode(r, { withTracks: true })),
    ],
  };
}





/* ------------------------------------------------------------ head helpers */

/**
 * Consistent canonical + Open Graph + Twitter meta for an indexable route.
 * Always emits absolute URLs and absolute image URLs.
 */
export function seoMeta(opts: {
  title: string;
  description: string;
  path: string;
  image?: string;
  imageAlt?: string;
  ogType?: "website" | "article" | "profile" | "book";
  extraMeta?: Array<Record<string, string>>;
}) {
  const { title, description, path, ogType = "website", extraMeta = [] } = opts;
  const url = absoluteUrl(path);
  const image = opts.image ? absoluteUrl(opts.image) : undefined;
  return {
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: ogType },
      { property: "og:url", content: url },
      { property: "og:site_name", content: SITE_NAME },
      ...(image
        ? [
            { property: "og:image", content: image },
            ...(opts.imageAlt ? [{ property: "og:image:alt", content: opts.imageAlt }] : []),
            { name: "twitter:image", content: image },
          ]
        : []),
      { name: "twitter:card", content: image ? "summary_large_image" : "summary" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      ...extraMeta,
    ],
    links: [{ rel: "canonical", href: url }],
  };
}

/* --------------------------------------------------------- press entities */

/** Stable @ids for the press surfaces. */
export const PRESS_KIT_ID = `${SITE_URL}/press#presskit`;
export const PRESS_RELEASES_ID = `${SITE_URL}/press/releases#pressreleases`;
export const releaseKitId = (slug: string) => `${SITE_URL}/press/kit/${slug}#presskit`;

export type NewsroomItem = {
  slug: string;
  title: string;
  dek: string;
  publishedAt: string;
  updatedAt?: string;
  authorName: string;
  category: string;
  kind?: "press-release" | "feature";
  image: { url: string; alt: string };
  body: string[];
  tags: string[];
  links: { to: string; label: string }[];
};

/**
 * A newsroom entry as JSON-LD.
 *
 * schema.org has no dedicated `PressRelease` type, so formal announcements are
 * modelled as `NewsArticle` carrying `genre: "Press release"` — the pattern
 * Google documents for wire announcements. Both variants reference the same
 * sitewide Person / Organization @ids so no duplicate entity is created.
 */
export function newsArticleNode(a: NewsroomItem): Node {
  const url = `${SITE_URL}/news/${a.slug}`;
  const image = absoluteUrl(a.image.url);
  const modified = a.updatedAt ?? a.publishedAt;
  const isRelease = a.kind === "press-release";
  return {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    "@id": `${url}#article`,
    headline: a.title,
    alternativeHeadline: a.dek,
    description: a.dek,
    datePublished: a.publishedAt,
    dateModified: modified,
    image: [image],
    thumbnailUrl: image,
    mainEntityOfPage: { "@type": "WebPage", "@id": url, url },
    url,
    inLanguage: "en-US",
    isPartOf: isRelease ? { "@id": PRESS_RELEASES_ID } : { "@id": NEWSROOM_ID },
    isAccessibleForFree: true,
    author: [personRef(), orgRef()],
    creator: personRef(),
    publisher: orgRef(),
    copyrightHolder: orgRef(),
    sourceOrganization: orgRef(),
    about: [personRef(), orgRef()],
    articleSection: a.category,
    ...(isRelease ? { genre: "Press release" } : {}),
    keywords: a.tags.join(", "),
    wordCount: a.body.join(" ").split(/\s+/).filter(Boolean).length,
    articleBody: a.body.join("\n\n"),
    ...(a.links.length
      ? {
          mentions: a.links.map((l) => ({
            "@type": "CreativeWork",
            name: l.label,
            url: absoluteUrl(l.to),
          })),
          relatedLink: a.links.map((l) => absoluteUrl(l.to)),
        }
      : {}),
  };
}

/**
 * A release-specific press kit page: a `ProfilePage`-style media resource that
 * points at the existing MusicAlbum/MusicRecording node by @id.
 */
export function releasePressKitNode(opts: {
  slug: string;
  title: string;
  description: string;
  image?: string;
  assets: Array<{ url: string; label: string }>;
}): Node {
  const url = `${SITE_URL}/press/kit/${opts.slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": releaseKitId(opts.slug),
    url,
    name: opts.title,
    description: opts.description,
    inLanguage: "en",
    isPartOf: { "@id": PRESS_KIT_ID },
    about: [{ "@id": releaseId(opts.slug) }, personRef(), orgRef()],
    mainEntity: { "@id": releaseId(opts.slug) },
    publisher: orgRef(),
    author: personRef(),
    ...(opts.image ? { primaryImageOfPage: { "@type": "ImageObject", url: opts.image } } : {}),
    ...(opts.assets.length
      ? {
          hasPart: opts.assets.map((a) => ({
            "@type": "MediaObject",
            name: a.label,
            contentUrl: a.url,
            creditText: "Courtesy of Buddha KA / KAfrequency",
            copyrightHolder: orgRef(),
          })),
        }
      : {}),
  };
}
